import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_CORETEX_APP, EnableCoretexAppInput } from '../data/mutations'

/**
 * Saves Coretex app's API key, username and password.
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const CoretexModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableCoretexApp, { loading }] = useMutation<
    { enableCoretexApp: { apiKey: string; password: string; username: string } },
    EnableCoretexAppInput
  >(ENABLE_CORETEX_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Coretex app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      if (err.message === 'Incorrect API key') {
        setFormError('Invalid api key or username/password. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid api key or username/password. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  type FieldValues = { apiKey: string; appPassword: string; appUsername: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableCoretexApp({
      variables: {
        apiKey: event.apiKey,
        appType: appType,
        password: event.appPassword,
        username: event.appUsername
      }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/coretex"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an admin Coretex account. Enter your Coretex API key, username and password.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'Coretex API Key',
            name: 'apiKey',

            placeHolder: 'Enter your Coretex API Key',
            type: 'input'
          },
          {
            labelText: 'Coretex username',
            name: 'appUsername',

            placeHolder: 'Enter your Coretex username',
            type: 'input'
          },
          {
            labelText: 'Coretex password',
            name: 'appPassword',

            placeHolder: 'Enter your Coretex password',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

CoretexModal.displayName = 'CoretexModal'

export { CoretexModal }
